import { get } from "@/infra/rest";
import { updateAgentSessionStep } from "@/modules/sessions/requests";
import {
  AgentSession,
  AgentSessionStatus,
  AgentSessionStep,
  AgentSessionStepType,
  AgentSessionType,
  CommonRagCreateRequest,
  CustomGapAgentSession,
  CustommizeControlsAgentSessionStep,
  MultiFileVendorRagCreateRequestInput,
  NistRiskAssessment,
  SOC2Type1GapAgentSession,
  SOC2Type2NewGapAgentSession,
} from "@/modules/sessions/types";
import { getSignedUrl } from "@/shared/requests/get-signed-url";
import { uploadFileReq } from "@/shared/requests/upload-file";
import { addNotification } from "@/shared/states/notification";
import { userStateSelector } from "@/shared/states/user";
import { AiResponseType } from "@/shared/types/user";
import { NavigateFunction } from "react-router-dom";
import { emitRagCreate } from "../requests";
import { getAgentData, getAgentStateActions, getAgentStepData } from "../states";
import {
  Control,
  CustomGapAssessmentControl,
  getCustomizeControls,
  getCustomizeControlsActions,
  getCustomizeControlsAgentType,
  Soc2Control,
  VendorAssessmentControl,
} from "../states/cusomize-controls";
import { AGENT_TYPES, GapAssessmentTypes, MultiVendorAssessmentTypes, RiskAssessmentTypes } from "../types";
import { getResponseTypes } from "../utils/get-response-type";
import { checkAndRefreshUrl } from "../utils/refreshUrl";
import { handleSocketResponse } from "../utils/socket-response";
import { sendThirdPartyVendorFormEmail } from "@/modules/vendors/requests";
import { ROUTES } from "@/shared/constants/routes";
import { v4 as uuid } from "uuid";

// Add a type for NIST CSF controls
export interface NistCsfControl {
  id?: string;
  uid: string;
  control_id: string;
  zania_control_id: string;
  function: string;
  category: string;
  subcategory: string;
  assessment_approach_presentation: string;
  assessment_approach: string;
  retriever_questions: string[];
  source_urls: string[];
  changesInControl?: number;
  edited_state: "default" | "custom" | "edited" | "NEW" | "ACCORDIAN";
  agentType: AgentSessionType.RISK_ASSESSMENT;
}

/**
 * Determines if an empty row should be shown for the given agent type and customization state
 */
export const shouldShowEmptyRow = (
  agentType: AgentSessionType | null,
  isCustomControls: boolean,
  searchQuery: string,
  showCheckboxes: boolean,
  isCurrentSessionInReassessment?: boolean
): boolean => {
  // Don't show empty row if search is active or checkboxes are shown
  if (searchQuery.trim().length > 0 || showCheckboxes) {
    return false;
  }

  // Don't show empty row when in reassessment mode
  if (isCurrentSessionInReassessment) {
    return false;
  }

  // Only show empty row when customization is enabled
  if (!isCustomControls) {
    return false;
  }

  // Show empty row for these agent types when customization is enabled
  const agentTypesWithEmptyRowsSet = new Set<AgentSessionType>([
    AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT,
    AgentSessionType.SOC2_GAP_TYPE1,
    AgentSessionType.SOC2_GAP_TYPE2,
    AgentSessionType.GAP_CUSTOM_FRAMEWORK
  ]);
  
  return agentTypesWithEmptyRowsSet.has(agentType as AgentSessionType);
  
};

/**
 * Checks if a control is an empty placeholder control
 */
export const isEmptyControl = (control: Control, agentType: AgentSessionType | null): boolean => {
  if (!control || control.edited_state !== "NEW") {
    return false;
  }

  switch (agentType) {
    case AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT:
      const vendorControl = control as VendorAssessmentControl;
      return !vendorControl.category && !vendorControl.question;
    
    case AgentSessionType.SOC2_GAP_TYPE1:
    case AgentSessionType.SOC2_GAP_TYPE2:
      const soc2Control = control as Soc2Control;
      return !soc2Control.tsc_id && !soc2Control.control;
    
    case AgentSessionType.GAP_CUSTOM_FRAMEWORK:
      const customControl = control as CustomGapAssessmentControl;
      return !customControl.control_id && !customControl.control;
    
    default:
      return false;
  }
};

export const handleContinueCustomizeControls = async (steps: AgentSessionStep[], sessionData: AgentSession) => {
  // Modify this call to avoid resetting agentType
  // Instead of calling reset() which sets everything to initial state including agentType: null
  const { reset, setAgentType, setIsCustomControls, setIsEvidenceMapping } = getCustomizeControlsActions();
  // Always reset the store when loading a new session to prevent old data from persisting
  reset();
  
  // Explicitly set the agentType from sessionData to ensure it's not null
  setAgentType(sessionData.type);
  setIsCustomControls(sessionData.customize)
  setIsEvidenceMapping((sessionData as SOC2Type1GapAgentSession)?.soc2_type1?.evidence_mapping || (sessionData as SOC2Type2NewGapAgentSession)?.soc2_type2?.evidence_mapping || (sessionData as NistRiskAssessment)?.risk_assessment?.evidence_mapping || (sessionData as CustomGapAgentSession)?.gap_custom_framework?.evidence_mapping ||false)
  
  const customizeControlStep = steps.find(
    (step) => 
      step.type === AgentSessionStepType.CONTROL_SELECTION || 
      step.type === AgentSessionStepType.NIST_CSF_EVIDENCE_MAPPING
  ) as CustommizeControlsAgentSessionStep | undefined;

  if (customizeControlStep && customizeControlStep.data && (customizeControlStep.status === AgentSessionStatus.INPUT_NEEDED)) {
    // Check for the new structure with metadata.control_sets
    let controlsUrl = customizeControlStep.data.url;

    // If url is null and metadata contains control_sets, use the controls_url from there
    if (!controlsUrl && customizeControlStep.data.metadata?.control_sets && customizeControlStep.data.metadata.control_sets.length > 0) {
      controlsUrl = customizeControlStep.data.url;
    }

    if (controlsUrl) {
      await processFileForCustomizeControls(controlsUrl, sessionData, customizeControlStep);
    }
  }
};

export const processFileForCustomizeControls = async (url: string | null | undefined, sessionData: AgentSession, controlStepDetails: any) => {
  if (!url) {
    throw new Error("Control set URL is null or undefined");
  }

  const validSignedUrl = await checkAndRefreshUrl(url);

  let customizeControlJson = await get<Control[]>({
    url: validSignedUrl,
    isAuthRequired: false,
  });

  if (sessionData.type === AgentSessionType.SOC2_GAP_TYPE1 || sessionData.type === AgentSessionType.SOC2_GAP_TYPE2) {
    customizeControlJson = (customizeControlJson as Soc2Control[]).map((control) => ({
      ...control,
      changesInControl: control.changesInControl === undefined ? 0 : control.changesInControl,
      changesInTestingProcedure:
        control.changesInTestingProcedure === undefined ? -1 : control.changesInTestingProcedure,
      toe: sessionData.type === AgentSessionType.SOC2_GAP_TYPE1 ? control.tod : control.toe,
    }));
  }

  if (sessionData.type === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
    customizeControlJson = (customizeControlJson as VendorAssessmentControl[]).map((control) => ({
      ...control,
      category: (control as any).key,
      key: undefined,
      changesInQuestion: control.changesInQuestion === undefined ? 0 : control.changesInQuestion,
    }));
  }

  if (sessionData.type === AgentSessionType.RISK_ASSESSMENT) {
    const nistControls = [];
    for (const control of customizeControlJson as unknown[]) {
      const ctrl = control as Record<string, unknown>;
      nistControls.push({
        ...ctrl,
        agentType: AgentSessionType.RISK_ASSESSMENT,
        uid: (ctrl.id || ctrl.control_id || ctrl.zania_control_id || ctrl.uid || '') as string,
        control_id: (ctrl.control_id || '') as string,
        zania_control_id: (ctrl.zania_control_id || '') as string,
        function: (ctrl.function || '') as string,
        category: (ctrl.category || '') as string,
        subcategory: (ctrl.subcategory || '') as string,
        assessment_approach_presentation: (ctrl.assessment_approach_presentation || '') as string,
        assessment_approach: (ctrl.assessment_approach || '') as string,
        retriever_questions: (ctrl.retriever_questions || []) as string[],
        source_urls: (ctrl.source_urls || []) as string[],
        edited_state: (ctrl.edited_state || "default") as string,
        changesInControl: ctrl.changesInControl === undefined ? 0 : ctrl.changesInControl as number,
      });
    }
    customizeControlJson = nistControls as unknown as Control[];
  }
  
  if (sessionData.type === AgentSessionType.GAP_CUSTOM_FRAMEWORK) {
    console.log("customizeControlJson is ", customizeControlJson)
    const columnsMetaData = controlStepDetails?.data.metadata;

    const customControls = [];
    for (const control of customizeControlJson as unknown[]) {
      const ctrl = control as Record<string, unknown>;

      // Create mapped control object from columnsMetaData
      const mappedControl: Record<string, unknown> = {};

      for (const column of columnsMetaData) {
        const value = ctrl[column.key] ?? ctrl[column.ui_key] ?? '';
        mappedControl[column.key] = value;
      }

      customControls.push({
        ...ctrl,
        ...mappedControl,
        agentType: AgentSessionType.GAP_CUSTOM_FRAMEWORK,
        control: mappedControl.control_description || "",
        tod: mappedControl.control_description,
        toe: mappedControl.testing_procedures || undefined,
        uid: (ctrl.uid  || uuid()) as string,
        source_urls: (ctrl.source_urls || []) as string[],
        edited_state: (ctrl.edited_state || "default") as string,
        changesInControl: ctrl.changesInControl === undefined ? 0 : ctrl.changesInControl as number,
        controlIdPrefix : (mappedControl?.control_id as string)?.match(/^[A-Za-z]+/)?.[0] || "",
        control_id: (mappedControl?.control_id as string)?.match(/\d+(?:\.\d+)*/)?.[0] ?? "",
      });
    }
    customizeControlJson = customControls as unknown as Control[];
  }

  const { setDataState, setAgentType, setSelectedTab, setShowCheckboxes } = getCustomizeControlsActions();

  setDataState(customizeControlJson);
  if (sessionData.type === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
    setSelectedTab("zania_security_assessment");
  }
  
  // Set checkboxes to false by default for all agent types
  setShowCheckboxes(false);

  setAgentType(sessionData.type);
};

/**
 * Transforms controls data for saving by filtering empty controls and applying necessary transformations
 */
export const transformControlsForSave = (agentType: AgentSessionType | null) => {
  // Filter out controls without meaningful content before processing
  const allControls = getCustomizeControls();
  const controlsWithId = allControls.filter((control) => {
    // For SOC2 controls, check if there's meaningful content beyond the default prefix
    if (agentType === AgentSessionType.SOC2_GAP || 
        agentType === AgentSessionType.SOC2_GAP_TYPE1 || 
        agentType === AgentSessionType.SOC2_GAP_TYPE2) {
      const soc2Control = control as any;
      const controlId = soc2Control.control_id || soc2Control.tsc_id || '';
      
      // Get the expected prefix for this control's TSC type
      const tsc = soc2Control.tsc;
      const expectedPrefix = tsc === 'security' ? 'CC' : 
                           tsc === 'availability' ? 'A' : 
                           tsc === 'confidentiality' ? 'C' : 
                           tsc === 'processing integrity' ? 'PI' : 
                           tsc === 'privacy' ? 'P' : '';
      
      // Check if there's meaningful content beyond just the prefix
      if (!controlId || controlId.trim().length === 0) {
        return false;
      }
      
      // Remove the prefix and check if there's actual content
      const contentBeyondPrefix = controlId.replace(expectedPrefix, '').trim();
      return contentBeyondPrefix.length > 0;
    }
    
    // For MULTI_FILE_VENDOR_ASSESSMENT, check category and question fields
    if (agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
      const vendorControl = control as any; // Type assertion since we know the agent type
      return vendorControl.category && vendorControl.category.trim().length > 0 &&
             vendorControl.question && vendorControl.question.trim().length > 0;
    }
    
    // For other agent types, check if control_id exists and is not empty
    if ('control_id' in control && control.control_id && control.control_id.trim().length > 0) {
      return true;
    }
    
    return false;
  });
  
  const controls = JSON.stringify(
    controlsWithId.map((d) => {
      const temp = { ...d, selected: true };

      if (
        agentType === AgentSessionType.SOC2_GAP_TYPE1 ||
        agentType === AgentSessionType.SOC2_GAP ||
        agentType === AgentSessionType.SOC2_GAP_TYPE2
      ) {
        (temp as Soc2Control).new_control = undefined;
        (temp as Soc2Control).new_tod = undefined;
        (temp as Soc2Control).new_toe = undefined;
      }
      if (agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
        (temp as VendorAssessmentControl).new_question = undefined;
        (temp as any).key = temp.category;
        (temp as any).category = undefined;
      }

      if(agentType === AgentSessionType.GAP_CUSTOM_FRAMEWORK){
        (temp as any)["Control Id"] = `${(d as any).controlIdPrefix} ${(d as any).control_id}`;
        (temp as any).control_id = `${(d as any).controlIdPrefix} ${(d as any).control_id}`;
        

        (temp as any).control_description = (d as Soc2Control).control;
        (temp as any)["Control Description"] = (d as Soc2Control).control;
        (temp as any)["Testing Procedures"] = (d as Soc2Control).toe;
        (temp as any).testing_procedures = (d as Soc2Control).toe;


        (temp as Soc2Control).new_control = undefined;
        (temp as Soc2Control).new_tod = undefined;
        (temp as Soc2Control).new_toe = undefined;
      }
      return temp;
    }),
  ); // temporary until backend is ready
  
  return controls;
};


export const handleAutoSaveCustomizeControls = async (agentId: string, markAsComplete = false) => {
  const agentType = getCustomizeControlsAgentType();
  // Get session data early to check for create_new_control_set flag
  const sessionData = getAgentData(agentId)?.sessionData;
  const isCreateNewControlSet = sessionData &&
    "multi_file_vendor_assessment" in sessionData &&
    sessionData.multi_file_vendor_assessment &&
    sessionData.multi_file_vendor_assessment.create_new_control_set === true;
  // Filter out controls without meaningful content before processing
  const allControls = getCustomizeControls();
  const controlsWithId = allControls.filter((control) => {
    // For SOC2 controls, check if there's meaningful content beyond the default prefix
    if (agentType === AgentSessionType.SOC2_GAP ||
        agentType === AgentSessionType.SOC2_GAP_TYPE1 ||
        agentType === AgentSessionType.SOC2_GAP_TYPE2) {
      const soc2Control = control as any;
      const controlId = soc2Control.control_id || soc2Control.tsc_id || '';
      // Get the expected prefix for this control's TSC type
      const tsc = soc2Control.tsc;
      const expectedPrefix = tsc === 'security' ? 'CC' :
                           tsc === 'availability' ? 'A' :
                           tsc === 'confidentiality' ? 'C' :
                           tsc === 'processing integrity' ? 'PI' :
                           tsc === 'privacy' ? 'P' : '';
      // Check if there's meaningful content beyond just the prefix
      if (!controlId || controlId.trim().length === 0) {
        return false;
      }
      // Remove the prefix and check if there's actual content
      const contentBeyondPrefix = controlId.replace(expectedPrefix, '').trim();
      return contentBeyondPrefix.length > 0;
    }
    // For MULTI_FILE_VENDOR_ASSESSMENT, check category and question fields
    if (agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
      const vendorControl = control as any; // Type assertion since we know the agent type
      return vendorControl.category && vendorControl.category.trim().length > 0 &&
             vendorControl.question && vendorControl.question.trim().length > 0;
    }
    // For other agent types, check if control_id exists and is not empty
    if ('control_id' in control && control.control_id && control.control_id.trim().length > 0) {
      return true;
    }
    return false;
  });
  // Already checked for create_new_control_set mode at the beginning
  const controls = JSON.stringify(
    controlsWithId.map((d) => {
      const temp = { ...d, selected: true };
      if (
        agentType === AgentSessionType.SOC2_GAP_TYPE1 ||
        agentType === AgentSessionType.SOC2_GAP ||
        agentType === AgentSessionType.SOC2_GAP_TYPE2
      ) {
        (temp as Soc2Control).new_control = undefined;
        (temp as Soc2Control).new_tod = undefined;
        (temp as Soc2Control).new_toe = undefined;
      }
      if (agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
        (temp as VendorAssessmentControl).new_question = undefined;
        (temp as any).key = temp.category;
        // Only clear category if not in create_new_control_set mode
        if (!isCreateNewControlSet) {
          (temp as any).category = undefined;
        }
      }
      if(agentType === AgentSessionType.GAP_CUSTOM_FRAMEWORK){
        (temp as any)["Control Id"] = `${(d as any).controlIdPrefix} ${(d as any).control_id}`;
        (temp as any).control_id = `${(d as any).controlIdPrefix} ${(d as any).control_id}`;
        (temp as any).control_description = (d as Soc2Control).control;
        (temp as any)["Control Description"] = (d as Soc2Control).control;
        if((d as Soc2Control).toe){
          (temp as any)["Testing Procedures"] = (d as Soc2Control).toe || undefined;
          (temp as any).testing_procedures = (d as Soc2Control).toe || undefined;
        }
        (temp as Soc2Control).new_control = undefined;
        (temp as Soc2Control).new_tod = undefined;
        (temp as Soc2Control).new_toe = undefined;
      }
      return temp;
    }),
  ); // temporary until backend is ready
  const blob = new Blob([controls], { type: "application/json" });
  const stepData = getAgentStepData(agentId);
  const { updateAgentStepData } = getAgentStateActions();
  const customizeControlStep = stepData?.find(
    (step) =>
      step.type === AgentSessionStepType.CONTROL_SELECTION ||
      step.type === AgentSessionStepType.NIST_CSF_EVIDENCE_MAPPING
  ) as CustommizeControlsAgentSessionStep | undefined;
  // Handle creating a new control set, but don't return early
  // so that the blob upload still happens
  if (isCreateNewControlSet && sessionData) {
    // Set up the UI but don't clear the data as it would erase user edits
    const { setAgentType, setSelectedTab, setShowCheckboxes } = getCustomizeControlsActions();
    setSelectedTab("zania_security_assessment");
    setShowCheckboxes(false);
    setAgentType(sessionData.type);
  }
  if (stepData && customizeControlStep && customizeControlStep.data) {
    let stepUrl = customizeControlStep.data.url;

    // Check if stepUrl is null before proceeding
    if (!stepUrl) {
      throw new Error("Control step URL is not available");
    }

    let currentStaleUrl = stepUrl;
    let markAsCompleteDone = false;
    if (!stepUrl.includes("signed_urls")) {
      console.log(stepUrl, "Fetching signed URL");
      const signedUrl = await getSignedUrl({
        file_names: [stepUrl],
        max_age: 86400,
      });
      const updatedStep = {
        ...customizeControlStep,
        data: {
          ...customizeControlStep.data,
          url: signedUrl[0],
        },
        status: markAsComplete ? AgentSessionStatus.COMPLETE : AgentSessionStatus.IN_PROGRESS,
      };
      const updatedSteps = stepData.map((step) => {
        if (step.id === updatedStep.id) {
          return updatedStep as AgentSessionStep;
        }
        return step as AgentSessionStep;
      });
      updateAgentStepData(agentId, updatedSteps as AgentSessionStep[]);
      // await updateAgentSessionStep(updatedStep as AgentSessionStep);
      currentStaleUrl = signedUrl[0];
      markAsCompleteDone = true;
    }
    const urlObj = new URL(currentStaleUrl);
    const expiryDate = urlObj.searchParams.get("se");
    if (expiryDate) {
      const expiry = new Date(expiryDate);
      const currentTime = new Date();
      const diff = expiry.getTime() - currentTime.getTime();
      if (diff < 0) {
        const signedUrl = await getSignedUrl({
          stale_urls: [currentStaleUrl],
          max_age: 86400,
        });
        currentStaleUrl = signedUrl[0];
      }
    }
    await uploadFileReq(currentStaleUrl, blob);
    // Always update the step data's URL regardless of markAsComplete
    // This ensures the URL is up to date after uploading the blob
    const updatedStep = {
      ...customizeControlStep,
      data: {
        ...customizeControlStep.data,
        url: currentStaleUrl,
        // Make sure metadata is preserved
        metadata: customizeControlStep.data.metadata
      },
      status: markAsComplete ? AgentSessionStatus.COMPLETE : customizeControlStep.status,
    };
    // Comment out API call but keep local state update
    // await updateAgentSessionStep(updatedStep as AgentSessionStep);
    const updatedStepData = stepData.map((step) => {
      if (step.id === updatedStep.id) {
        return updatedStep;
      }
      return step;
    });
    // Update local state with the new URL and ensure controls data is retained
    updateAgentStepData(agentId, updatedStepData as AgentSessionStep[]);
    // If this is a create_new_control_set session, make sure we persist the controls data
    // This is critical for the user-edited controls to remain visible
    if (isCreateNewControlSet && customizeControlStep.data) {
      // We don't need to do anything additional here - the blob upload will persist the controls
      // and the local state has already been updated with the correct URL
      console.log("Updated controls in create_new_control_set mode");
    }
  }
};

export const handleGenerateResponse = async (agentId: string, navigate: NavigateFunction) => {
  const agentTypeFromCustomControlsStore = getCustomizeControlsAgentType();
  if (
    agentTypeFromCustomControlsStore === AgentSessionType.SOC2_GAP ||
    agentTypeFromCustomControlsStore === AgentSessionType.SOC2_GAP_TYPE1 ||
    agentTypeFromCustomControlsStore === AgentSessionType.SOC2_GAP_TYPE2
  ) {
    const agentData = getAgentData<AGENT_TYPES.GAP_ASSESSMENT, GapAssessmentTypes.SOC2>(agentId);

    if (!agentData) {
      throw new Error("An error occurred");
    }
    const { updateAgentStepData } = getAgentStateActions();

    const selectedResponse =
      userStateSelector.getState().aiResponseType === AiResponseType.NONE
        ? AiResponseType.LITE
        : userStateSelector.getState().aiResponseType;
    const task = agentData.sessionData.type === AgentSessionType.SOC2_GAP_TYPE1 ? "soc2_type1" : "soc2_type2";
    const statusInProgressSessionStep = [
      AgentSessionStepType.SOC2_TYPE2_AUDIT,
      AgentSessionStepType.SOC2_TYPE2,
      AgentSessionStepType.SOC2_TYPE1,
    ];

    const statusCompleteSessionSteps = [
      AgentSessionStepType.CONTROL_SELECTION
    ];

    const steps = agentData.stepData.reduce((acc, step) => {
      if (statusCompleteSessionSteps.includes(step.type)) {
        step.status = AgentSessionStatus.COMPLETE;
      }

      if (statusInProgressSessionStep.includes(step.type)) {
        step.status = AgentSessionStatus.IN_PROGRESS;
      }

      acc.push(step);
      return acc;
    }, [] as AgentSessionStep[]);
    const customizeControlStep = steps.find((step) => step.type === AgentSessionStepType.CONTROL_SELECTION);
    const { response_mode, response_quality } = getResponseTypes(selectedResponse);
    if (customizeControlStep) {
      const source_urls =
        agentData.sessionData.type === AgentSessionType.SOC2_GAP
          ? agentData.sessionData.soc2_type2_audit.source_urls
          : agentData.sessionData.type === AgentSessionType.SOC2_GAP_TYPE1
            ? agentData.sessionData.soc2_type1.source_urls
            : agentData.sessionData.soc2_type2.source_urls;

      const controlsUrl = (customizeControlStep as CustommizeControlsAgentSessionStep)?.data?.url ?? "";

      const gapAssessmentRequest: CommonRagCreateRequest = {
        doc_type: "json",
        response_quality,
      controls_url: controlsUrl,
      source_urls,
      evidence_mapping: true,
      }; const agent_session_step_id = steps.find((step) => statusInProgressSessionStep.includes(step.type))?.id ?? "";

      updateAgentStepData(agentId, steps);

      // await updateAgentSessionStep(customizeControlStep);

      await emitRagCreate(
        {
          agent_session_id: agentId,
          agent_session_step_id,
          rag_input: gapAssessmentRequest,
          response_mode,
          task: task,
        },
        (response: any) => {
          handleSocketResponse(response, navigate);
        },
      );
    }
  }
  if (agentTypeFromCustomControlsStore === AgentSessionType.GAP_CUSTOM_FRAMEWORK) {
    const agentData = getAgentData<AGENT_TYPES.GAP_ASSESSMENT, GapAssessmentTypes.GAP_CUSTOM_FRAMEWORK>(agentId);

    if (!agentData) {
      throw new Error("An error occurred");
    }
    const { updateAgentStepData } = getAgentStateActions();

    const selectedResponse =
      userStateSelector.getState().aiResponseType === AiResponseType.NONE
        ? AiResponseType.LITE
        : userStateSelector.getState().aiResponseType;
    const task = AgentSessionType.GAP_CUSTOM_FRAMEWORK;
    const statusInProgressSessionStep = [
      AgentSessionStepType.GAP_CUSTOM_FRAMEWORK,
    ];

    const statusCompleteSessionSteps = [
      AgentSessionStepType.CONTROL_SELECTION
    ];

    const steps = agentData.stepData.reduce((acc, step) => {
      if (statusCompleteSessionSteps.includes(step.type)) {
        step.status = AgentSessionStatus.COMPLETE;
      }

      if (statusInProgressSessionStep.includes(step.type)) {
        step.status = AgentSessionStatus.IN_PROGRESS;
      }

      acc.push(step);
      return acc;
    }, [] as AgentSessionStep[]);
    const customizeControlStep = steps.find((step) => step.type === AgentSessionStepType.CONTROL_SELECTION);
    const { response_mode, response_quality } = getResponseTypes(selectedResponse);
    if (customizeControlStep) {
      const source_urls = agentData.sessionData.gap_custom_framework.source_urls;

      const controlsUrl = (customizeControlStep as CustommizeControlsAgentSessionStep)?.data?.url ?? "";

      const gapAssessmentRequest: CommonRagCreateRequest = {
        doc_type: "json",
        response_quality,
        controls_url: controlsUrl,
        source_urls,
        evidence_mapping: true,
      };

      if(agentData.sessionData.gap_custom_framework){
        // Custom GAP Assessment
        gapAssessmentRequest.framework_id = agentData.sessionData.gap_custom_framework.framework_id;
        gapAssessmentRequest.agent_type = agentData.agentType;
      }

      const agent_session_step_id = steps.find((step) => statusInProgressSessionStep.includes(step.type))?.id ?? "";

      updateAgentStepData(agentId, steps);

      // await updateAgentSessionStep(customizeControlStep);

      await emitRagCreate(
        {
          agent_session_id: agentId,
          agent_session_step_id,
          rag_input: gapAssessmentRequest,
          response_mode,
          task: task,
        },
        (response: any) => {
          handleSocketResponse(response, navigate);
        },
      );
    }
  }
  if (agentTypeFromCustomControlsStore === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
    const selectedResponse =
      userStateSelector.getState().aiResponseType === AiResponseType.NONE
        ? AiResponseType.LITE
        : userStateSelector.getState().aiResponseType;
    const agentData = getAgentData<
      AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
      MultiVendorAssessmentTypes.VENDOR_ASSESSMENT
    >(agentId);

    if (!agentData) {
      throw new Error("An error occurred");
    }

    let { stepData, sessionData } = agentData;
    const { setAgentData } = getAgentStateActions();
    
    
    const vendorProfileStep = stepData.find((step) => step.type === AgentSessionStepType.MULTI_VENDOR_PROFILE);
    if (!vendorProfileStep) {
      throw new Error("Vendor profile step not found");
    }
    const sourceUrls = vendorProfileStep.data?.source_urls || [];
    const { response_mode, response_quality } = getResponseTypes(selectedResponse);

    // Check each URL and only refresh the expired ones
    const refreshedUrls = await Promise.all(
      sourceUrls.map(async (url) => {
        return await checkAndRefreshUrl(url);
      }),
    );
    const customizeControlStep = stepData.find((step) => step.type === AgentSessionStepType.CONTROL_SELECTION) as CustommizeControlsAgentSessionStep | undefined;
    const controlsUrl = customizeControlStep?.data?.url ?? "";

    const vendorAssessment: MultiFileVendorRagCreateRequestInput = {
      doc_type: "json",
      response_quality,
      source_urls: refreshedUrls,
      vendor_intake_form: vendorProfileStep.data?.vendor_intake_form,
      controls_url: controlsUrl,
    };

    // Check if vendor_filled_intake_form is true and send email accordingly, when vendor submits the Questionnaire, the create task will be triggered automatically
    if (sessionData.multi_file_vendor_assessment?.vendor_filled_intake_form) {
      try {
        await sendThirdPartyVendorFormEmail({
          agent_session_id: agentId,
          vendor_email: sessionData.multi_file_vendor_assessment.vendor_email as string,
          contact_email: sessionData.multi_file_vendor_assessment.contact_email as string,
          vendor_name: sessionData.multi_file_vendor_assessment.vendor_name,
          ui_redirect_url: `${window.location.origin}/vendor-security-form/`,
          request_type: 'initial_request',
        });

        setAgentData(agentId, {
          ...agentData,
          sessionData: {
            ...sessionData,
            status: AgentSessionStatus.FORM_SENT,
          }
        });
        navigate?.(`/${ROUTES.AGENT}/${agentId}`, { replace: true });
      } catch (error) {
        const apiErrorDetails = (error as any)?.response?.data?.detail?.[0];
        if (apiErrorDetails) {
          addNotification({
            message: `Failed to send vendor form email. ${apiErrorDetails.loc.join()} - ${apiErrorDetails.msg}`,
            type: "error",
          });
        } else {
          addNotification({
            message: "Unexpected error occurred. Please try again.",
            type: "error",
          });
        };
        // Don't navigate away on error, allow user to retry
        return;
      }
    } else {

      stepData = stepData.map((step) => {
      if (
        [
          AgentSessionStepType.LOAD_TEMPLATE,
          AgentSessionStepType.EXTRACT_CONTROLS,
          AgentSessionStepType.MULTI_VENDOR_PROFILE,
          AgentSessionStepType.CONTROL_SELECTION,
        ].includes(step.type)
      ) {
        step.status = AgentSessionStatus.COMPLETE;
      }

      const inprogresStep = AgentSessionStepType.MULTI_FILE_VENDOR_ASSESSMENT;

      if ((step.type as AgentSessionStepType) === inprogresStep) {
        step.status = AgentSessionStatus.IN_PROGRESS;
      }
      return step;
    });

    setAgentData(agentId, {
      ...agentData,
      stepData,
    });

    const multiFileVendorAssessmentStep = stepData.find(
      (step): step is AgentSessionStep =>
        (step.type as AgentSessionStepType) === AgentSessionStepType.MULTI_FILE_VENDOR_ASSESSMENT,
    );

    const agent_session_step_id = multiFileVendorAssessmentStep?.id ?? "";

      // Proceed with RAG creation if no vendor form email is to be sent
      emitRagCreate(
        {
          agent_session_id: agentId,
          agent_session_step_id,
          rag_input: vendorAssessment,
          response_mode,
          task: "multi_file_vendor_assessment",
          vendor_intake_form: vendorProfileStep.data?.vendor_intake_form,
        },
        async (response: any) => {
          handleSocketResponse(response, navigate);
        },
      );
    }
  
  }
  if (agentTypeFromCustomControlsStore === AgentSessionType.RISK_ASSESSMENT) {
    
    const selectedResponse =
    userStateSelector.getState().aiResponseType === AiResponseType.NONE
      ? AiResponseType.LITE
      : userStateSelector.getState().aiResponseType;
  const agentData = getAgentData<
    AGENT_TYPES.RISK_ASSESSMENT,
    RiskAssessmentTypes.NIST_CSF_2
  >(agentId);

  if (!agentData) {
    throw new Error("An error occurred");
  }
  let { stepData } = agentData;

  const generateRiskAssessmentResponseStep = stepData.find((step) => step.type === AgentSessionStepType.GENERATE_RISK_ASSESSMENT_RESPONSE);
    if (!generateRiskAssessmentResponseStep) {
      throw new Error("Generate risk assessment response step not found");
    }
  
    const { response_mode, response_quality } = getResponseTypes(selectedResponse);

    const evidenceMappingStep = stepData.find((step) => step.type === AgentSessionStepType.NIST_CSF_EVIDENCE_MAPPING) as CustommizeControlsAgentSessionStep | undefined;

    const controlsUrl = evidenceMappingStep?.data?.url ?? "";

    const riskAssessmentRequest: CommonRagCreateRequest = {
      doc_type: "json",
      response_quality,
      controls_url: controlsUrl,
      source_urls: [],
      evidence_mapping: true,
    };

    stepData = stepData.map((step) => {
      if (step.type === AgentSessionStepType.NIST_CSF_EVIDENCE_MAPPING ||
        step.type === AgentSessionStepType.LOAD_TEMPLATE ||
        step.type === AgentSessionStepType.EXTRACT_CONTROLS) {
        step.status = AgentSessionStatus.COMPLETE;
      }

      if (step.type === AgentSessionStepType.GENERATE_RISK_ASSESSMENT_RESPONSE) {
        step.status = AgentSessionStatus.IN_PROGRESS;
      }
      return step;
    });

    const { setAgentData } = getAgentStateActions();
    setAgentData(agentId, {
      ...agentData,
      stepData,
    });

    const generateRiskAssessmentResponseStep1 = stepData.find(
      (step): step is AgentSessionStep =>
        (step.type as AgentSessionStepType) === AgentSessionStepType.GENERATE_RISK_ASSESSMENT_RESPONSE,
    );

    const agent_session_step_id = generateRiskAssessmentResponseStep1?.id ?? "";

    try {
      if (evidenceMappingStep && evidenceMappingStep.id) {
        // Cast the evidenceMappingStep to AgentSessionStep to ensure it has all required properties
        const updatedStep: AgentSessionStep = {
          ...evidenceMappingStep as any,
          status: AgentSessionStatus.COMPLETE,
        };
        await updateAgentSessionStep(updatedStep);
      }
    } catch (error) {
      console.error("Error updating agent session step:", error);
    }
    
    emitRagCreate(
      {
        agent_session_id: agentId,
        agent_session_step_id,
        rag_input: riskAssessmentRequest,
        response_mode,
        task: "nist_csf_2_risk_assessment",
      },
      (response: any) => {
        handleSocketResponse(response, navigate);
      },
    );
    

  }
};
